import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import api from "@/lib/api";
import { confirmAction } from "@/lib/confirm";

export const FOLDER_COLORS = [
  "#E05236", "#4A5D4E", "#D4A373", "#7D8C96", "#0A0A0A",
  "#5B67E7", "#B4478A", "#3F8AB2", "#DBB84D", "#7C4A79",
];

export default function FolderModal({ open, onOpenChange, folder, onSaved, onDeleted }) {
  const { t } = useTranslation();
  const isEdit = !!folder;
  const [name, setName] = useState("");
  const [color, setColor] = useState(FOLDER_COLORS[0]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (open) {
      setName(folder?.name || "");
      setColor(folder?.color || FOLDER_COLORS[0]);
    }
  }, [open, folder]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      const payload = { name: name.trim(), color };
      const { data } = isEdit
        ? await api.patch(`/folders/${folder.folder_id}`, payload)
        : await api.post("/folders", payload);
      toast.success(isEdit ? t("folders.updated") : t("folders.created"));
      onSaved?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isEdit) return;
    if (!(await confirmAction({ message: t("folders.confirmDelete"), destructive: true }))) return;
    setDeleting(true);
    try {
      await api.delete(`/folders/${folder.folder_id}`);
      toast.success(t("folders.deleted"));
      onDeleted?.(folder.folder_id);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="folder-modal"
        className="rounded-sm max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tight">
            {isEdit ? t("folders.editTitle") : t("folders.newTitle")}
          </DialogTitle>
          <DialogDescription className="text-sm text-neutral-600">
            {t("folders.modalHint")}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div>
            <Label htmlFor="folder-name" className="text-xs uppercase tracking-[0.15em]">
              {t("folders.name")}
            </Label>
            <Input
              id="folder-name"
              data-testid="folder-name-input"
              value={name}
              autoFocus
              maxLength={60}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder={t("folders.namePlaceholder")}
              className="mt-2 h-11 rounded-sm"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.15em]">{t("folders.color")}</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {FOLDER_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  data-testid={`folder-color-${c}`}
                  onClick={() => setColor(c)}
                  className={`w-8 h-8 rounded-sm transition-transform ${
                    color === c ? "ring-2 ring-offset-2 ring-neutral-950 scale-110" : ""
                  }`}
                  style={{ background: c }}
                  aria-label={c}
                />
              ))}
            </div>
          </div>
          <DialogFooter className="pt-2 flex sm:justify-between gap-2">
            {isEdit ? (
              <Button
                type="button"
                variant="ghost"
                onClick={handleDelete}
                disabled={deleting || saving}
                data-testid="folder-delete-btn"
                className="rounded-sm text-red-600 hover:text-red-700 hover:bg-red-50 h-11"
              >
                <Trash2 className="w-4 h-4 mr-1.5" />
                {t("common.delete")}
              </Button>
            ) : (
              <span />
            )}
            <Button
              type="submit"
              disabled={saving || !name.trim()}
              data-testid="folder-submit-btn"
              className="rounded-sm bg-[#E05236] hover:bg-[#C94A31] text-white h-11 px-5"
            >
              {saving ? "..." : isEdit ? t("common.save") : t("folders.create")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
